/**
 * The **datasets** pane — spec §F5.2 (second half), as **client specification S3** rebuilt
 * it. Top→bottom:
 *   0. the theme's own state, where it has one — provisional, in preparation, display-only
 *   1. *Filter datasets* input (live)
 *   2. a **Variables** `<select>`, where the theme's default is a MELS baseline variable
 *   3. a **Raster dataset** `<select>` — one drawn raster at a time — and, while one is
 *      drawn, its opacity and metadata under it
 *   4. a **Vector datasets** `<select>` that *adds*, with everything added listed under it
 *
 * ## What S3 changed
 *
 * **The word.** Outside the rail's Indicators icon the explorer says *datasets*, never
 * *indicators* (S3 clause 5) — labels, placeholders, empty states and toasts, en and fr.
 * Nothing about the §D5 catalogue changed; the pane is a list of datasets and now says so.
 *
 * **The theme decides the list.** The rail used to narrow this pane by §C1 domain; it now
 * selects a *theme*, and a theme is its domain plus whatever `/meta/config.theme_defaults`
 * names for it. The three theme states the client's table carries are printed rather than
 * inferred: Water is flagged provisional with the client's own sentence, Digital Agriculture
 * shows the in-preparation state **and no selects at all**, and Ecosystem opens its
 * display-only default with the note that says why it cannot be analysed.
 *
 * **Baseline variables are datasets here.** GESI opens on the MELS extension pair and SLM on
 * the twelve crop-yield rows; those are §C `indicators` rows by storage and survey
 * measurements by nature, and the client's table names them as the themes' defaults. They
 * get a select of their own, fed by the config's list, so no contractual or thematic
 * indicator can reach it: the server says which ids a theme opens on, and this pane offers
 * exactly those.
 *
 * **Since the 8 Sep 2026 directive that is also how a baseline chip works.** The ten workbook
 * sheets are themes on the rail (`DomainRail`) and rows in `theme_defaults.themes`, so one of
 * them arrives here as a theme whose `indicators` are its members and whose `layers` are
 * empty — the pane needs no branch for it. **All** carries every baseline variable for the
 * same reason it always carried GESI's and SLM's: the client's ruling is "all datasets
 * excluding indicators", and a baseline variable is a dataset. The eight domains keep only the
 * two the client's own table names; the server enforces that (`themes._domain_variables`).
 *
 * **A series is one row, not nine.** `lc_annual` carries eight published epochs; the eight
 * per-year rows are addressed by the map's time slider rather than listed beside their own
 * series (`features/map/epochs.ts`).
 *
 * ## The 2026-08-23 client correction (D2), in this file
 *
 * > *"In the Layers pane the dropdowns select ACTUAL LAYERS: a 'Raster layer' select (one
 * > visible raster at a time — the single pick IS the rule; option None; optgroups by domain;
 * > unavailable rows disabled) and a 'Vector layers' select that ADDS a vector on pick."*
 *
 * The split is the point, and it is a split by **what the reader can have at once** rather
 * than by §D5 `kind`: two opaque tile layers stack, so a raster is a single-valued choice and
 * a listbox is literally that type; vectors combine freely, so their control is an *add*
 * whose result is a list. R4's "one raster at a time" therefore stops being a rule enforced
 * over a list of checkboxes (disable every other raster row, explain why, hope no other writer
 * forgets) and becomes the shape of the control: picking a second raster **replaces** the
 * first (`setActiveRaster`, one write), and *None* is how you draw none.
 *
 * That deletes a whole class of state from the pane — `raster-busy` rows, their tooltip and
 * their toast — and with it the reader-facing question "why is this row dead?" for the one
 * case where nothing was actually wrong. The two states where something *is* wrong keep their
 * place in the list as **disabled options carrying the reason**: §I2 `access: 'none'` says
 * which classification locks it, §D5 `unavailable` says the source is not wired up yet. They
 * stay listed because a layer that vanishes reads as a portal that lost it.
 *
 * Data: `['layers', role]` (role-dependent — see `queryKeys.layers`) and `['config']` for the
 * domain palette and order. Both filters — the free-text box and the R3 domain rail — narrow
 * the **options only**, never the map: a layer that is on stays drawn while it is filtered out
 * of the picker, and stays visible in its own select (`PaneSelect`'s `fallback`), because a
 * control that reads *None* over a drawn layer is a control lying about the map.
 *
 * The §F5.6(d) legend blocks, the permalink and C1's boundary panel are untouched: all three
 * read and write `layersOn`, which is still the one fact about what is drawn.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import {
  fetchConfig,
  fetchIndicators,
  fetchLayers,
  queryKeys,
  type Domain,
  type Layer,
} from '../../lib/api';
import {
  DEFAULT_LAYER_OPACITY,
  effectiveLevels,
  ROLE_NAMES,
  selectYear,
  useAppStore,
} from '../../lib/state';
import { toast } from '../../lib/toast';
import { Chip, type ChipKind } from '../../components/Chip';
import {
  drawnEpochOf,
  epochDate,
  epochLabel,
  epochMemberIds,
  epochsForYear,
  epochsOf,
  isDateSeries,
  NDVI_SERIES,
  seriesFor,
  snapEpoch,
} from '../map/epochs';
import { isRasterKind } from '../map/layerKinds';
import { leafLabel, partitionBaselineGroups } from './baselineGroups';
import { PaneSelect, type PaneOptGroup } from './PaneSelect';
import { YearSelect } from './YearSelect';
import { matchesLayerQuery, sourceKindLine } from './layerKind';
import {
  layerInTheme,
  seriesLocked,
  themeCapability,
  themeDefaults,
  themeEntry,
  themeHiddenLayers,
  themeOf,
  themeState,
} from './themes';

export function LayersPane() {
  const { t } = useTranslation();
  const role = useAppStore((s) => s.role);
  const query = useAppStore((s) => s.layerQuery);
  const setLayerQuery = useAppStore((s) => s.setLayerQuery);
  const layersOn = useAppStore((s) => s.layersOn);
  const domFilter = useAppStore((s) => s.domFilter);
  const indicator = useAppStore((s) => s.indicator);
  const setIndicator = useAppStore((s) => s.setIndicator);
  const toggleLayer = useAppStore((s) => s.toggleLayer);
  const setActiveRaster = useAppStore((s) => s.setActiveRaster);
  // Task 7: the per-epoch date pick (`ndvi_terra`) and the crop-mask stencilled over it —
  // both meaningless for a legacy series, which still moves entirely through `layersOn`.
  const epochOn = useAppStore((s) => s.epochOn);
  const setEpoch = useAppStore((s) => s.setEpoch);
  const ndviMask = useAppStore((s) => s.ndviMask);
  const setNdviMask = useAppStore((s) => s.setNdviMask);
  const yearChosen = useAppStore((s) => s.yearChosen);
  const year = useAppStore(selectYear);

  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });
  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });

  const theme = themeOf(domFilter);
  const themeRow = useMemo(() => themeEntry(config, theme), [config, theme]);
  const state = themeState(themeRow);
  /**
   * What this theme can offer at all (client directive, 9 Sep 2026) — see `themeCapability`.
   *
   * Read before the option lists are built rather than after: an empty `<select>` is the
   * dead control the client reported, and the difference between "this theme has no rasters"
   * and "your filter matched no raster" is the difference between a missing section and a
   * *No match* note. The first is this; the second is `noMatch`, below.
   */
  /**
   * The 9 Sep 2026 second directive lives in the same call, as its one extra fact.
   *
   * > *"Raster dataset / Select dataset / Vector datasets must not render wherever a
   * > choropleth is displayed."*
   *
   * `indicator` is exactly "a choropleth is displayed": it is the one store field that paints
   * the hierarchy, and both families land in it — a contractual indicator under the icon and a
   * MELS baseline variable under GESI, SLM or a baseline chip. Yesterday's capability could
   * not see it, so the two domain themes that open on a survey variable kept both §D5 selects
   * over a choropleth of something else. See `themeCapability` for why the controls come back
   * rather than being lost.
   */
  const can = useMemo(
    () => themeCapability(config, theme, themeRow, { choropleth: Boolean(indicator) }),
    [config, theme, themeRow, indicator],
  );
  /** The ids this theme does not list — the 9 Sep one-epoch switch, read off the server. */
  const hidden = useMemo(() => themeHiddenLayers(themeRow), [themeRow]);

  /** R3: the rail's pick narrows this pane. S3: it is a theme rather than a bare domain. */
  const domains = useMemo(
    () => (config?.domains ?? []).filter((d) => !domFilter || d.id === domFilter),
    [config, domFilter],
  );
  const domainById = useMemo(
    () => new Map((config?.domains ?? []).map((d) => [d.id, d])),
    [config],
  );
  const byId = useMemo(() => new Map((layers ?? []).map((l) => [l.id, l])), [layers]);
  /** A series' per-year rows are the slider's, not the list's — see `features/map/epochs.ts`. */
  const epochIds = useMemo(() => epochMemberIds(layers), [layers]);

  /**
   * The theme's MELS baseline variables, as one group of dataset options.
   *
   * The ids come from the server's table and nothing widens them: a contractual or a
   * thematic indicator cannot appear here however the catalogue is ordered, because the
   * only ids this reads are the ones `/meta/config.theme_defaults` names for this theme.
   */
  const variables = useMemo(() => {
    const wanted = themeDefaults(themeRow).variables;
    if (!wanted.length) return [];
    const known = new Map((indicators ?? []).map((i) => [i.id, i]));
    return wanted
      .map((id) => known.get(id))
      .filter((row): row is NonNullable<typeof row> => Boolean(row));
  }, [themeRow, indicators]);

  /**
   * The variables select, headed by sheet **only where the theme spans more than one**.
   *
   * A theme whose variables all come off one workbook sheet is a list, not a taxonomy: GESI's
   * gendered pair, SLM's twelve crops and every baseline chip's own sheet each get the plain
   * *Variables* heading they have always had, with the series' full names in it. **All** is
   * the case that needed the change: since the 8 Sep directive it offers every baseline
   * variable (a baseline variable is a dataset — the client's own ruling), and fifty-eight
   * rows under one heading is exactly the flat wall observation P5 was about. There it uses
   * `partitionBaselineGroups` — the same clustering the §F5.2 picker got — so each sheet is
   * its own heading and the option reads *Mulching* rather than *Soil practice — mulching*.
   */
  const variableGroups: PaneOptGroup[] = useMemo(() => {
    if (!variables.length) return [];
    const q = query.trim().toLowerCase();
    // The free-text box narrows this group too, on the two facets a variable has: its name
    // and its full name. `matchesLayerQuery`'s haystack is built from §D5 columns a §C row
    // does not carry, so the predicate is written out rather than the row faked into a `Layer`.
    const surviving = variables.filter(
      (v) => !q || `${v.name} ${v.full_name ?? ''}`.toLowerCase().includes(q),
    );
    if (!surviving.length) return [];
    const option = (v: (typeof surviving)[number], label: string) => ({
      value: v.id,
      label: `${label}${v.active ? '' : ` (${t('indicators.demoSuffix')})`}`,
      disabled: !v.active,
      title: v.active ? (v.full_name ?? v.name) : t('indicators.inactiveTitle'),
    });
    const sheets = new Set(surviving.map((v) => v.group_id ?? ''));
    if (sheets.size < 2) {
      return [{ label: t('layers.variables'), options: surviving.map((v) => option(v, v.name)) }];
    }
    const { grouped, ungrouped } = partitionBaselineGroups(surviving);
    return [
      ...(ungrouped.length
        ? [{ label: t('layers.variables'), options: ungrouped.map((v) => option(v, v.name)) }]
        : []),
      ...grouped.map((group) => ({
        // The sheet *and* which load it is, as the picker's own headings read: "Agroforestry"
        // alone beside a domain name would read as a domain.
        label: `${group.label} · ${t('indicators.groupSuffix')}`,
        options: group.members.map((v) => option(v, leafLabel(v.name))),
      })),
    ];
  }, [variables, query, t]);

  /**
   * The two option lists: `domainId → surviving rows`, in the server's order, split by
   * whether the layer reaches the map as tiles or as geometry.
   *
   * A domain with no surviving row is dropped — an `<optgroup>` heading over nothing is
   * noise, and it is the same rule the Indicators pane applies to an empty tier.
   */
  const { rasters, vectors } = useMemo(() => {
    const nameOf = new Map(domains.map((d) => [d.id, d.name]));
    const pick = (isRaster: boolean): PaneOptGroup[] =>
      domains
        .map((domain) => ({
          label: domain.name,
          options: (layers ?? [])
            .filter(
              (layer) =>
                layer.domain === domain.id &&
                layerInTheme(layer, theme, themeRow) &&
                // A year of a series is reached through the map's time slider, never as a
                // ninth row beside its own series (`features/map/epochs.ts`).
                !epochIds.has(layer.id) &&
                // …and the rows the theme's own switch hides — Land Use's five other
                // land-cover products since the 9 Sep directive.
                !hidden.has(layer.id) &&
                isRasterKind(layer.kind) === isRaster &&
                matchesLayerQuery(layer, query, nameOf.get(layer.domain)),
            )
            .map((layer) => ({
              value: layer.id,
              label: optionLabel(layer, t),
              // §I2 and §D5: offered, dead, and saying which of the two it is. A disabled
              // `<option>` raises no event, so the reason has to be *in* it.
              disabled: layer.access === 'none' || Boolean(layer.unavailable),
              title:
                layer.access === 'none'
                  ? t('layers.lockedTitle')
                  : (layer.note ?? undefined),
            })),
        }))
        .filter((group) => group.options.length > 0);
    return { rasters: pick(true), vectors: pick(false) };
  }, [domains, layers, query, theme, themeRow, epochIds, hidden, t]);

  /**
   * What is drawn, split the same way the two selects are.
   *
   * Derived from **this pane's own catalogue** rather than from the store's raster register
   * (`selectActiveRaster`): the register is written by whoever holds the map's copy of
   * `['layers', role]`, and this pane must not render "None" over a drawn raster in the
   * moment before — or in a context where — that component has not mounted. Both read the
   * same query, so they agree; the difference is only that this one cannot be early.
   */
  const activeVectors = useMemo(
    () => (layers ?? []).filter((l) => layersOn.has(l.id) && !isRasterKind(l.kind)),
    [layers, layersOn],
  );
  /**
   * The drawn raster **as the list names it** — the series row where a year of one is on.
   *
   * Stepping the time slider to 2019 puts `lca2019` on the map, and `lca2019` is not in this
   * pane's options (it is a year, not a dataset). Without the resolution the select would
   * read *No raster dataset* over a drawn raster, which is the exact lie `fallback` exists
   * to prevent. `pickRaster` never sees this value — it is what the control *displays*.
   */
  const drawnRaster = useMemo(
    () => (layers ?? []).find((l) => layersOn.has(l.id) && isRasterKind(l.kind)) ?? null,
    [layers, layersOn],
  );
  const activeRasterLayer = useMemo(
    () => (drawnRaster && epochIds.has(drawnRaster.id)
      ? (seriesFor(layers, drawnRaster.id) ?? drawnRaster)
      : drawnRaster),
    [layers, drawnRaster, epochIds],
  );
  const activeRaster = activeRasterLayer?.id ?? null;
  /**
   * Which ids the raster select is allowed to replace — this pane's own reading of §D5's
   * `kind`, handed to the store so *None* and a swap work whether or not the map's copy of
   * the catalogue has registered them yet (`setActiveRaster`).
   */
  const rasterIds = useMemo(
    () => (layers ?? []).filter((l) => isRasterKind(l.kind)).map((l) => l.id),
    [layers],
  );

  /**
   * The beside-the-dataset year drop-down for a drawn raster series (client, 2026-09-15) —
   * `#epochSelect`, same condition `EpochSlider` uses to draw its bar: a series of at least
   * two published epochs, the drawn raster is one of them (or the series row itself), and the
   * series is not switched off (`seriesLocked`, the 9 Sep land-cover directive).
   *
   * Task 7 widens this for a **date series** (`ndvi_terra`): `dateSeries` picks which of the
   * two vocabularies `#epochSelect`'s options and `onChange` use (an ISO date vs. a per-year
   * layer id), and `list` is the epochs `epochOn`/`#epochDate`/the step arrows are allowed to
   * land on — every published epoch, or just `year`'s when the reader has chosen one
   * (`yearChosen`). `cadence` is read off the series once so the "Year"/"Period" label, the
   * arrows and the date input's `type` all agree with each other.
   */
  const epochSeries = seriesFor(layers, drawnRaster?.id ?? null);
  const dateSeries = epochSeries ? isDateSeries(epochSeries) : false;
  const fullEpochs = epochsOf(epochSeries);
  const drawnEpoch = epochSeries ? drawnEpochOf(epochSeries, layersOn, epochOn) : null;
  const list = epochSeries ? epochsForYear(epochSeries, yearChosen ? year : null) : [];
  const listIdx = drawnEpoch
    ? list.findIndex((e) => epochDate(e) === epochDate(drawnEpoch))
    : -1;
  const showEpochSelect =
    Boolean(epochSeries) &&
    fullEpochs.length >= 2 &&
    Boolean(drawnEpoch) &&
    !seriesLocked(config, epochSeries!.id);
  // `cadence` is `null`/`undefined` for every legacy series (`lc_annual`) as well as an
  // explicit `'annual'` — both read the same "Year" label and get no arrows/date input, so
  // the two are folded into one flag rather than compared to the literal string twice.
  const cadence = epochSeries?.cadence;
  const annualCadence = !cadence || cadence === 'annual';
  const showEpochNav = showEpochSelect && !annualCadence;
  const epochSeriesId = epochSeries?.id ?? null;

  /**
   * Task 7: narrowing `#epochSelect` to `year` can leave the drawn epoch outside the list
   * it now offers (a date series can be on any epoch when the reader picks a year). Snap
   * once — to the year's newest epoch — rather than leaving the control silently disagreeing
   * with the map. Recomputed from scratch inside the effect (not off the outer `list`) so it
   * fires exactly on a year/series change and is a no-op every other render, including the
   * one right after its own `setEpoch`/`setActiveRaster` write.
   *
   * F6: a **legacy annual series** (`lc_annual`) narrows the same way — the guard used to
   * read `!dateSeries || …` and skip it entirely, which left the Layers pane showing (say)
   * 2019's row while the year filter said 2021. `setActiveRaster` is the legacy write
   * (`onEpochSelectChange`/`stepEpoch` already branch on `dateSeries` the same way).
   */
  useEffect(() => {
    if (!epochSeries || !yearChosen) return;
    const narrowed = epochsForYear(epochSeries, year);
    if (!narrowed.length) return;
    const drawn = drawnEpochOf(epochSeries, layersOn, epochOn);
    const inYear = drawn && narrowed.some((e) => epochDate(e) === epochDate(drawn));
    if (inYear) return;
    const latest = narrowed[narrowed.length - 1];
    if (!latest) return;
    if (dateSeries) setEpoch(epochSeries.id, epochDate(latest));
    else setActiveRaster(latest.layer, rasterIds);
    // Keyed on the year and series id only (see the comment above this effect); everything
    // else read inside is re-derived fresh each run rather than tracked as a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, epochSeriesId, yearChosen]);

  /** `#epochSelect`'s own vocabulary: an ISO date for a date series, a layer id otherwise. */
  const epochOptionValue = (epoch: (typeof fullEpochs)[number]) =>
    dateSeries ? epochDate(epoch) : epoch.layer;
  const selectedEpoch = listIdx >= 0 ? list[listIdx] : (list[list.length - 1] ?? null);
  const epochSelectValue = selectedEpoch ? epochOptionValue(selectedEpoch) : '';
  const epochSelectOptions = list.length
    ? list.map((epoch) => ({ value: epochOptionValue(epoch), label: epochLabel(epoch, cadence) }))
    : [{ value: '', label: t('layers.noEpochInYear', { year }) }];
  const onEpochSelectChange = (value: string) => {
    if (!epochSeries || !list.length) return;
    if (dateSeries) setEpoch(epochSeries.id, value);
    else setActiveRaster(value, rasterIds);
  };
  /** A step of `#epochPrev`/`#epochNext` — always within the (possibly year-narrowed) list. */
  const stepEpoch = (i: number) => {
    const target = list[i];
    if (!epochSeries || !target) return;
    if (dateSeries) setEpoch(epochSeries.id, epochDate(target));
    else setActiveRaster(target.layer, rasterIds);
  };
  const canStepPrev = listIdx > 0;
  const canStepNext = listIdx >= 0 && listIdx < list.length - 1;
  /** `#epochDate`: a plain day for 8d/dekad, a `YYYY-MM` month for a monthly cadence. */
  const epochDateInputType: 'date' | 'month' = cadence === 'month' ? 'month' : 'date';
  const asInputValue = (iso: string) =>
    epochDateInputType === 'month' ? iso.slice(0, 7) : iso;
  const epochDateValue = selectedEpoch ? asInputValue(epochDate(selectedEpoch)) : '';
  const epochDateMin = list.length ? asInputValue(epochDate(list[0]!)) : undefined;
  const epochDateMax = list.length ? asInputValue(epochDate(list[list.length - 1]!)) : undefined;
  const onEpochDateChange = (raw: string) => {
    if (!epochSeries || !list.length || !raw) return;
    const iso = epochDateInputType === 'month' ? `${raw}-01` : raw;
    const snapped = snapEpoch(list, iso);
    if (!snapped) return;
    if (dateSeries) setEpoch(epochSeries.id, epochDate(snapped));
    else setActiveRaster(snapped.layer, rasterIds);
  };

  /**
   * `#ndviMask` — the GEOGLAM-BACS crop-mask stencil, offered only over a drawn NDVI series
   * and only once the catalogue actually carries the three mask rows with a
   * `stencil_template` (`RasterLayers.tsx` renders the stencil itself; this is just the
   * picker). `byId` is this pane's own catalogue map, already built above.
   */
  const ndviDrawn = Boolean(
    drawnRaster && (NDVI_SERIES as readonly string[]).includes(drawnRaster.id),
  );
  const MASK_IDS = ['mask_maize', 'mask_rice', 'mask_soy'] as const;
  const hasMaskCatalogue = MASK_IDS.every((id) => Boolean(byId.get(id)?.stencil_template));
  const showNdviMask = ndviDrawn && hasMaskCatalogue;

  /**
   * §I2/§D5/§I1 in one place, for both selects.
   *
   * The two refusals are unreachable through the control (their options are `disabled`), and
   * they are here anyway: `disabled` is the browser's guarantee for a mouse and a keyboard,
   * not for a programmatic `change`, and this is the only place that knows *why* a row is
   * dead. The third case is not a refusal at all — §I1's LIMITED is a caveat on the way in,
   * said once, because repeating it on every switch-off trains the reader to dismiss the one
   * message that qualifies what they are seeing.
   */
  const admits = (layer: Layer): boolean => {
    if (layer.access === 'none') {
      toast(
        t('layers.lockedToast', {
          name: layer.name,
          classification: t(`layers.class.${layer.classification}`, layer.classification),
          role: t(`role.${role}`, ROLE_NAMES[role]),
        }),
      );
      return false;
    }
    if (layer.unavailable) {
      toast(t('layers.unavailableToast', { name: layer.name, note: layer.note ?? '' }));
      return false;
    }
    if (layer.access === 'limited') toast(t('layers.limitedToast'));
    return true;
  };

  /** D2: the single pick **is** the one-raster rule. `''` is the select's *None* option. */
  const pickRaster = (id: string) => {
    if (!id) {
      setActiveRaster(null, rasterIds);
      return;
    }
    const layer = byId.get(id);
    if (!layer || !admits(layer)) return;
    setActiveRaster(id, rasterIds);
  };

  /** D2: the vector select **adds**, and snaps back to its placeholder (see `value=""`). */
  const addVector = (id: string) => {
    if (!id) return;
    const layer = byId.get(id);
    if (!layer || layersOn.has(id) || !admits(layer)) return;
    toggleLayer(id, true);
  };

  /**
   * S3: the theme's default *variable*, where it has one. `''` unmaps it.
   *
   * Mapping a variable clears the raster for the same reason the two pane tabs used to
   * clear each other (round 4 E2): a choropleth under an opaque raster is a map the reader
   * cannot see, and the panel below is about one of them.
   */
  const pickVariable = (id: string) => {
    if (!id) {
      setIndicator(null);
      return;
    }
    const row = variables.find((v) => v.id === id);
    if (!row) return;
    if (!row.active) {
      toast(t('indicators.inactiveTitle'));
      return;
    }
    setActiveRaster(null, rasterIds);
    setIndicator(id, row.has_watershed_values, effectiveLevels(row));
  };

  const activeVariable = variables.find((v) => v.id === indicator) ?? null;
  const noMatch =
    query.trim().length > 0 && !rasters.length && !vectors.length && !variableGroups.length;

  return (
    <div className="sb-pane active" id="pane-layers">
      {/*
        The theme's own state, in the client's own sentence, before anything the reader can
        pick. `in_preparation` prints instead of the selects rather than above them — the
        client asked for "an in-preparation state, no dataset", and a filter box over an
        empty catalogue is a control with nothing to do.
      */}
      {/*
        **A note without a state still prints** (client review, 2026-09-08). This was gated
        on `state` alone, and the three states are Water's `provisional`, Ecosystem's
        `display_only` and Digital Agriculture's `in_preparation` — so a theme that carried
        only a sentence had it computed, translated, served and dropped.

        Land Use was the theme that made the bug visible and is no longer one of them: the
        client had its sentence removed outright on 9 Sep 2026, so that row now carries no
        note at all (`apps/meta/themes.py`). The rule stays because Climate still needs it,
        and because a served note that nothing renders is the same defect wherever it lands.

        The state stays the emphasised half when there is one; `data-themestate` is still
        written only for a real state, so a note-only theme is `[data-themenote]` and the
        three state assertions keep meaning what they meant.
      */}
      {state || themeRow?.note ? (
        <div
          className={`note ${state === 'in_preparation' ? 'warn' : 'info'}`}
          {...(state ? { 'data-themestate': state } : { 'data-themenote': theme })}
        >
          {state ? <b>{t(`themes.state.${state}`)}</b> : null}
          {themeRow?.note ? <>{state ? ' ' : ''}{themeRow.note}</> : null}
        </div>
      ) : null}

      {!can.any ? null : (
        <>
      <div className="sb-section">
        <div className="ins-head">{t('sidebar.filterDatasets')}</div>
        <input
          className="std"
          id="layerFilter"
          type="search"
          value={query}
          placeholder={t('sidebar.filterDatasetsPh')}
          aria-label={t('sidebar.filterDatasets')}
          onChange={(e) => setLayerQuery(e.target.value)}
        />
        {noMatch ? (
          <div className="note info" data-nomatch="">
            {t('layers.noMatch', { query })}
          </div>
        ) : null}
      </div>

      {can.variables && variableGroups.length ? (
        <div className="sb-section">
          <div className="ins-head">{t('layers.variables')}</div>
          <PaneSelect
            id="variableSelect"
            label={t('layers.variables')}
            showLabel={false}
            value={activeVariable?.id ?? ''}
            fallback={activeVariable?.name}
            placeholder={t('layers.noVariable')}
            groups={variableGroups}
            onChange={pickVariable}
          />
        </div>
      ) : null}

      {/*
        The two §D5 selects, **only for a theme that reaches the catalogue and is not showing
        a choropleth** (client directives, 9 Sep 2026, first and second rounds). A baseline
        sheet fails the first test: its row names no layers and its key is not a §C1 domain,
        so both lists were always the placeholder alone. GESI, SLM, a baseline chip and the
        Indicators icon fail the second whenever a variable is mapped — dataset furniture over
        a choropleth points at a map that is not on screen. See `themeCapability`; both tests
        are on the server's row plus one store field, so nothing here knows what a baseline
        sheet is.
      */}
      {can.layers ? (
        <>
      <div className="sb-section">
        <div className="ins-head">{t('layers.rasterLayer')}</div>
        <PaneSelect
          id="rasterSelect"
          label={t('layers.rasterLayer')}
          showLabel={false}
          value={activeRaster ?? ''}
          fallback={activeRasterLayer?.name}
          placeholder={t('layers.noRaster')}
          groups={rasters}
          onChange={pickRaster}
          lead={
            <span
              className="dot"
              data-domdot={activeRasterLayer?.domain ?? ''}
              aria-hidden="true"
              style={{
                background: activeRasterLayer
                  ? (domainById.get(activeRasterLayer.domain)?.color ?? 'var(--ink-3)')
                  : 'transparent',
              }}
            />
          }
        />
        {/*
          The card describes the row that is **drawn** — `lca2019` while the slider is on
          2019 — but its *name line* is the series' own name where one applies, not the
          epoch's: `#epochSelect` right below already shows which year, and client 2026-09-15
          asks for "a general name - Land cover, then the year can show when a user filters
          by year" — a name line reading "Land cover 2019" would repeat the year the select
          already carries and drop the constant name the client asked for. The opacity slider
          still keys on the drawn id itself, so it moves what the reader can actually see.
        */}
        {drawnRaster ? (
          <ActiveLayer
            layer={drawnRaster}
            displayName={epochSeries?.name}
            domain={domainById.get(drawnRaster.domain)}
            onOff={() => setActiveRaster(null, rasterIds)}
          />
        ) : null}
        {/*
          Client, 2026-09-15: the same "drop down beside the dataset" directive as
          `#yearSelect`, for a series' epochs — `EpochSlider`'s bar (now behind `#btnAnimate`)
          is the animation, this is the everyday control. A legacy series writes
          `setActiveRaster` with the epoch's own layer id, exactly as before; a **date**
          series (Task 7) writes `setEpoch` with the picked epoch's own ISO date instead,
          since every one of its epochs draws through the series' own id.
        */}
        {showEpochSelect ? (
          <YearSelect
            id="epochSelect"
            label={annualCadence ? t('layers.epoch') : t('layers.epochStep')}
            value={epochSelectValue}
            options={epochSelectOptions}
            onChange={onEpochSelectChange}
            disabled={!list.length}
          />
        ) : null}
        {/*
          Task 7: the date picker and step arrows beside `#epochSelect`, for any series whose
          cadence is finer than a year — a legacy series (`cadence` `null`/`'annual'`) has no
          "day within the year" to step through, so it keeps `#epochSelect` alone, as before.
        */}
        {showEpochNav ? (
          <div className="epoch-nav">
            <button
              type="button"
              id="epochPrev"
              className="mapbtn"
              aria-label={t('layers.prev')}
              disabled={!canStepPrev}
              onClick={() => stepEpoch(listIdx - 1)}
            >
              ‹
            </button>
            <input
              id="epochDate"
              className="std"
              type={epochDateInputType}
              aria-label={t('layers.epochDate')}
              value={epochDateValue}
              min={epochDateMin}
              max={epochDateMax}
              onChange={(e) => onEpochDateChange(e.target.value)}
            />
            <button
              type="button"
              id="epochNext"
              className="mapbtn"
              aria-label={t('layers.next')}
              disabled={!canStepNext}
              onClick={() => stepEpoch(listIdx + 1)}
            >
              ›
            </button>
          </div>
        ) : null}
        {/*
          Task 7: the GEOGLAM-BACS crop-mask stencil (`RasterLayers.tsx` draws it) — offered
          only while an NDVI series is actually drawn and the catalogue carries the three
          mask rows with a `stencil_template`.
        */}
        {showNdviMask ? (
          <YearSelect
            id="ndviMask"
            label={t('layers.ndviMask')}
            value={ndviMask ?? ''}
            options={[
              { value: '', label: t('layers.maskNone') },
              { value: 'mask_maize', label: t('layers.maskMaize') },
              { value: 'mask_rice', label: t('layers.maskRice') },
              { value: 'mask_soy', label: t('layers.maskSoy') },
            ]}
            onChange={(value) => setNdviMask(value || null)}
          />
        ) : null}
      </div>

      <div className="sb-section">
        <div className="ins-head">{t('layers.vectorLayers')}</div>
        <PaneSelect
          id="vectorSelect"
          label={t('layers.vectorLayers')}
          showLabel={false}
          // Never carries a value: it adds, and what it added is the list below.
          value=""
          placeholder={t('layers.addVector')}
          groups={vectors}
          onChange={addVector}
        />
        <div id="vectorActive">
          {activeVectors.map((layer) => (
            <ActiveLayer
              key={layer.id}
              layer={layer}
              domain={domainById.get(layer.domain)}
              onOff={() => toggleLayer(layer.id, false)}
            />
          ))}
        </div>
      </div>
        </>
      ) : null}
        </>
      )}
    </div>
  );
}

/** §I2/§D5 in the option text — the chip and the lock icon a row used to carry. */
function optionLabel(layer: Layer, t: TFunction): string {
  if (layer.access === 'none') {
    return t('layers.optLocked', {
      name: layer.name,
      classification: t(`layers.class.${layer.classification}`, {
        defaultValue: layer.classification,
      }),
    });
  }
  if (layer.unavailable) return t('layers.optUnavailable', { name: layer.name });
  return layer.name;
}

export interface ActiveLayerProps {
  layer: Layer;
  domain?: Domain;
  onOff: () => void;
  /**
   * Overrides `layer.name` on the name line only — everything else (id, opacity, the ✕)
   * still keys on `layer`. Used for a drawn series member: `#epochSelect` beside this card
   * already names the year, so the card's own name reads the series' name instead of the
   * epoch's own dataset title. See the call site's comment.
   */
  displayName?: string;
}

/**
 * One drawn layer — the half of §F5.2's row that is an *action on something already chosen*:
 * the live opacity slider, the row's own note, *View metadata →* (the §F8 dataset record,
 * opened in place over the map), and the ✕ that removes it.
 *
 * It is deliberately not a checkbox any more. What is in this list is what is on, so a tick
 * box would be a control that can only ever be un-ticked — and the two ways to switch a layer
 * off (here and §F5.6(d)'s legend ✕) already share one store action, so they cannot disagree.
 */
function ActiveLayer({ layer, domain, onOff, displayName }: ActiveLayerProps) {
  const { t } = useTranslation();
  const opacity = useAppStore((s) => s.layerOpacity[layer.id] ?? DEFAULT_LAYER_OPACITY);
  const setLayerOpacity = useAppStore((s) => s.setLayerOpacity);
  // Client item 6: *View metadata →* opens the §F8 record over the map (`app/RecordHost.tsx`)
  // rather than announcing that the catalogue is coming. It names the layer's **dataset**,
  // not the layer: §D5 keeps the two ids apart, and the record is the dataset's.
  const openRecord = useAppStore((s) => s.openRecord);
  const pct = Math.round(opacity * 100);
  const name = displayName ?? layer.name;
  const off = t('layers.turnOff', { name });

  return (
    <div className="lay-active" data-layer={layer.id}>
      <div className="lay-active-head">
        <span
          className="dot"
          aria-hidden="true"
          style={{ background: domain?.color ?? 'var(--ink-3)' }}
        />
        <span className="lay-name">
          {name}
          <small>{sourceKindLine(layer)}</small>
        </span>
        {layer.is_provisional ? (
          <Chip kind="provisional" title={t('layers.provisionalTitle')}>
            {t('layers.provisional')}
          </Chip>
        ) : null}
        <Chip kind={layer.classification as ChipKind}>
          {t(`layers.class.${layer.classification}`, layer.classification)}
        </Chip>
        <button
          type="button"
          className="lay-off"
          data-layoff={layer.id}
          title={off}
          aria-label={off}
          onClick={onOff}
        >
          ✕
        </button>
      </div>
      <div className="opacity-line">
        <label htmlFor={`layer-op-${layer.id}`}>{t('layers.opacity')}</label>
        <input
          id={`layer-op-${layer.id}`}
          type="range"
          min={10}
          max={100}
          value={pct}
          data-op={layer.id}
          aria-label={t('layers.opacityFor', { name })}
          onChange={(e) => setLayerOpacity(layer.id, Number(e.target.value) / 100)}
        />
        <span style={{ fontFamily: 'var(--mono)', width: 32, textAlign: 'right' }}>{pct}%</span>
      </div>
      <div className="layer-desc">
        {layer.note ??
          t('layers.descFallback', {
            source: layer.source_org,
            domain: domain?.name ?? layer.domain,
          })}
      </div>
      <button
        type="button"
        className="layer-meta-btn"
        data-meta={layer.id}
        onClick={() => openRecord(layer.dataset_id)}
      >
        {t('layers.viewMetadata')}
      </button>
    </div>
  );
}

export default LayersPane;
